import React, { useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  Scale,
  Wrench,
  Stethoscope,
  Landmark,
  Briefcase,
  Megaphone,
  Map as MapIcon,
  FileText,
  Stamp,
  BadgeCheck,
  FileCheck,
  Upload,
  ListChecks,
  Search,
  Calculator,
  CreditCard,
  ClipboardCheck,
  Send,
  GraduationCap,
  Building,
  Star,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { catalogTypeIds } from '../data/navServices';
import { mainServicesByGroup } from '../data/mainServices';
import { interpretingServices, getInterpretingService } from '../data/interpretingServices';
import { useSubServices } from '../data/subServicesStore';
import ServicePriceRows from '../components/common/ServicePriceRows';
import ServiceCatalog from '../components/services/ServiceCatalog';
import HowItWorks from '../components/home/HowItWorks';
import assetUrl from '../assetUrl';
import './ServicesPage.css';
import './AllServicesPage.css';

// Icônes locales par slug — la structure des services vient de services.json
const TYPE_ICONS = {
  general: FileText, specialized: BadgeCheck, legal: Scale, technical: Wrench,
  medical: Stethoscope, financial: Landmark, commercial: Briefcase, marketing: Megaphone,
  tourism: MapIcon, certified: BadgeCheck, sworn: Stamp, legalization: FileCheck,
  apostille: Stamp, personal: FileText, administrative: Building, academic: GraduationCap,
  legalDocs: Scale, commercialDocs: Briefcase, medicalDocs: Stethoscope,
};

const fromJson = (groupId) => mainServicesByGroup(groupId).map((s) => ({
  id: s.slug,
  icon: TYPE_ICONS[s.slug] || FileText,
  image: s.image,
  // Noms/descriptions du services.json (source de vérité, fr/en/ar)
  names: { fr: s.name, en: s.nameEn || '', ar: s.nameAr || '' },
  descs: { fr: s.description || '', en: s.descriptionEn || '', ar: s.descriptionAr || '' },
  fallback: { title: s.name, cardDesc: s.description || '' },
}));

const PROFESSIONAL = fromJson('professional');
const OFFICIAL = fromJson('official');
const DOCUMENTS = fromJson('documents');

// Interprétariat : enregistré dans config/interpreting.json — source unique
// (labels fr/en/ar, descriptions, images).

function ServiceCard({ id, icon: Icon, image, type, variant, discover }) {
  if (!type) return null;
  return (
    <Link to={`/services/type/${id}`} className={`services-hub-card services-hub-card--${variant}`}>
      {image ? (
        <img
          src={assetUrl(`${image}?v=12`)}
          alt=""
          loading="lazy"
          decoding="async"
          style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '0.5rem', marginBottom: '0.75rem' }}
        />
      ) : (
        <span className="services-hub-card-icon" aria-hidden="true">
          <Icon size={24} strokeWidth={1.75} />
        </span>
      )}
      <h3>{type.title}</h3>
      <p>{type.cardDesc}</p>
      <span className="services-hub-card-cta">
        {discover}
        <ArrowRight size={16} aria-hidden="true" />
      </span>
    </Link>
  );
}

function InterpretingCard({ image, type, priceInfo, locale, t }) {
  if (!type) return null;
  // Prix uniquement si données DB (serveur en ligne) — sinon carte sans prix
  const hasPrice = !!priceInfo && (priceInfo.price > 0
    || Object.values(priceInfo.prices || {}).some((v) => Number(v) > 0));
  const reviewCount = priceInfo?.rating || 0;
  const reviewLabel = reviewCount.toLocaleString(locale);
  return (
    <div className="all-services-card">
      <div className="all-services-card-media">
        <img
          src={assetUrl(`${image}?v=12`)}
          alt=""
          loading="lazy"
          decoding="async"
        />
        {hasPrice && (
          <Link to="/order" className="asc-order" aria-label="Commander en ligne">
            <span>Commander</span>
          </Link>
        )}
      </div>
      <div className="all-services-card-body">
        <h3>{type.title}</h3>
        {hasPrice ? (
          <>
            <div className="all-services-card-rating">
              <div className="all-services-card-stars" aria-label={`${reviewLabel} ${t.serviceCatalog.reviews}`}>
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    fill={reviewCount > 0 ? 'currentColor' : 'none'}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <span className="all-services-card-reviews">
                {reviewLabel} {t.serviceCatalog.reviews}
              </span>
            </div>
            <ServicePriceRows service={priceInfo} locale={locale} />
          </>
        ) : (
          type.cardDesc ? <p>{type.cardDesc}</p> : null
        )}
      </div>
    </div>
  );
}

export default function ServicesPage() {
  const { t, lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const activeType = catalogTypeIds.includes(type) ? type : null;

  const hub = t.servicesHub || {};
  const types = useMemo(() => t.serviceTypes?.types || {}, [t.serviceTypes?.types]);
  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-GB' : 'fr-FR';


  const [query, setQuery] = useState('');

  // `name`/`nameEn`/`nameAr` + descriptions des configs = source de vérité ; i18n en fallback
  const cardType = (item) => {
    const i18n = types[item.id];
    const jsonTitle = item.names?.[lang];
    const jsonDesc = item.descs?.[lang];
    if (item.fallback || item.names) {
      return { title: jsonTitle || i18n?.title || item.fallback?.title, cardDesc: jsonDesc || i18n?.cardDesc || item.fallback?.cardDesc || '' };
    }
    return i18n;
  };

  // Interprétariat : DB (prix, notes) si le serveur répond — sinon interpreting.json sans prix
  const interpSubs = useSubServices('interpreting');

  const interpretingList = useMemo(() => {
    const fromDb = (interpSubs.items || [])
      .filter((s) => getInterpretingService(s.slug)) // slugs enregistrés dans interpreting.json
      .map((s) => {
        const cfg = getInterpretingService(s.slug);
        return {
        id: cfg.slug,
        image: s.image || cfg.image || '/images/interpreting-simultaneous.jpg',
        names: {
          fr: s.titleFr || cfg.name || '',
          en: s.titleEn || cfg.nameEn || '',
          ar: s.titleAr || cfg.nameAr || '',
        },
        descs: {
          fr: s.description || cfg.description || '',
          en: cfg.descriptionEn || '',
          ar: cfg.descriptionAr || '',
        },
        fallback: { title: s.titleFr || cfg.name || s.slug, cardDesc: s.description || cfg.description || '' },
        priceInfo: {
          price: s.basePrice, priceMax: s.priceMax, unit: s.unit,
          prices: s.prices || {}, priceEur: s.priceEur || 0, typePricing: s.typePricing || [],
          rating: s.reviewsCount || 0,
        },
      };
    });
    const base = fromDb.length
      ? fromDb
      : interpretingServices.map((s) => ({
          id: s.slug,
          image: s.image,
          names: { fr: s.name, en: s.nameEn || '', ar: s.nameAr || '' },
          descs: { fr: s.description || '', en: s.descriptionEn || '', ar: s.descriptionAr || '' },
          fallback: { title: s.name, cardDesc: s.description || '' },
          priceInfo: null,
        }));
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((item) => {
      const itemType = types[item.id] || {};
      const text = `${itemType.title || item.fallback.title} ${itemType.cardDesc || item.fallback.cardDesc || ''}`.toLowerCase();
      return text.includes(q);
    });
  }, [interpSubs.items, query, types]);

  if (!type) {
    return <Navigate to="/tous-les-services" replace />;
  }

  // « Interprétariat » : hub affichant les 7 types d'interprétariat
  if (type === 'interpreting') {
    const interpType = types.interpreting || {};
    return (
      <div className="services-page all-services-page">
        <header
          className="all-services-hero"
          style={{
            backgroundImage:
              `linear-gradient(135deg, rgba(12, 30, 60, 0.92) 0%, rgba(21, 42, 74, 0.88) 60%, rgba(26, 122, 122, 0.78) 100%), ` +
              `url(${assetUrl('/images/interpreting-simultaneous.jpg')})`,
          }}
        >
          <div className="all-services-hero-glow" aria-hidden="true" />
          <div className="container all-services-hero-inner">
            <h1>{interpType.pageTitle || interpType.title || t.nav?.interpreting}</h1>
            <p>{interpType.pageLead || interpType.cardDesc}</p>
            <div className="all-services-search" style={{ marginTop: '24px', maxWidth: '560px', marginLeft: 'auto', marginRight: 'auto' }}>
              <Search size={18} aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un type d'interprétation..."
                aria-label="Rechercher un type d'interprétation"
              />
            </div>
          </div>
        </header>

        <section className="all-services-count-row">
          <div className="container">
            <span className="all-services-count-badge">
              <strong>{interpretingList.length}</strong> services disponibles
            </span>
          </div>
        </section>

        <section className="all-services-catalog all-services-catalog--narrow" style={{ paddingTop: 12 }}>
          <div className="container">
            <div className="all-services-grid">
              {interpretingList.map((item) => (
                <InterpretingCard
                  key={item.id}
                  image={item.image}
                  type={cardType(item)}
                  priceInfo={item.priceInfo}
                  locale={locale}
                  t={t}
                />
              ))}
            </div>
          </div>
        </section>

        <HowItWorks showHeader showCta={false} />

        <section className="all-services-cta-banner">
          <div className="container">
            <h2>Besoin d&apos;une traduction ou d&apos;un interprète ?</h2>
            <p>Commandez en ligne ou contactez-nous pour un devis personnalisé.</p>
            <div className="all-services-cta-actions">
              <Link to="/order" className="btn btn-primary">
                Commander en ligne
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Nous contacter
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (activeType) {
    return <ServiceCatalog forcedType={activeType} hideTypeFilters />;
  }

  const processIcons = [Upload, ListChecks, Search, Calculator, CreditCard, ClipboardCheck, Send];

  return (
    <div className="services-page">
      {/* Hero */}
      <header className="services-hero">
        <div className="services-hero-glow" aria-hidden="true" />
        <div className="container services-hero-inner">
          <h1>{t.services?.title || hub.heroTitle}</h1>
          <p>{hub.heroSubtitle || t.services?.subtitle}</p>
          <div className="services-hero-actions">
            <Link to="/order" className="btn btn-primary">
              {hub.heroOrder}
            </Link>
            <Link to="/contact" className="btn btn-outline">
              {hub.heroContact}
            </Link>
          </div>
        </div>
      </header>

      {/* Professional translation */}
      <section className="services-hub-section">
        <div className="container">
          <div className="services-hub-section-head">
            <span className="services-hub-badge">1</span>
            <div>
              <h2>{hub.profTitle}</h2>
              <p>{hub.profIntro}</p>
            </div>
          </div>
          <div className="services-hub-grid services-hub-grid--3">
            {PROFESSIONAL.map((item) => (
              <ServiceCard key={item.id} {...item} type={cardType(item)} variant="professional" discover={hub.discover} />
            ))}
          </div>
        </div>
      </section>

      {/* Official translation */}
      <section className="services-hub-section services-hub-section--alt">
        <div className="container">
          <div className="services-hub-section-head">
            <span className="services-hub-badge services-hub-badge--alt">2</span>
            <div>
              <h2>{hub.officialTitle}</h2>
              <p>{hub.officialIntro}</p>
            </div>
          </div>
          <div className="services-hub-grid services-hub-grid--4">
            {OFFICIAL.map((item) => (
              <ServiceCard key={item.id} {...item} type={cardType(item)} variant="official" discover={hub.discover} />
            ))}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="services-hub-section">
        <div className="container">
          <div className="services-hub-section-head">
            <span className="services-hub-badge">3</span>
            <div>
              <h2>{hub.docsTitle}</h2>
              <p>{hub.docsIntro}</p>
            </div>
          </div>
          <div className="services-hub-grid services-hub-grid--3">
            {DOCUMENTS.map((item) => (
              <ServiceCard key={item.id} {...item} type={cardType(item)} variant="documents" discover={hub.discover} />
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="services-hub-section services-hub-section--process">
        <div className="container">
          <div className="services-hub-section-head services-hub-section-head--center">
            <h2>{hub.processTitle}</h2>
            <p>{hub.processIntro}</p>
          </div>
          <div className="services-hub-process">
            {hub.processSteps?.map(({ title, desc }, index) => {
              const Icon = processIcons[index] || FileCheck;
              return (
                <div key={title} className="services-hub-step">
                  <span className="services-hub-step-number">{index + 1}</span>
                  <span className="services-hub-step-icon" aria-hidden="true">
                    <Icon size={20} />
                  </span>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              );
            })}
          </div>
          <div className="services-hub-process-link">
            <Link to="/about#processus" className="btn btn-outline">
              {hub.processCta || t.about?.processTitle}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="services-cta">
        <div className="container services-cta-inner">
          <h2>{hub.finalTitle}</h2>
          <p>{hub.finalText}</p>
          <div className="services-cta-actions">
            <Link to="/order" className="btn btn-primary">
              {hub.finalOrder}
            </Link>
            <Link to="/contact" className="btn btn-outline">
              {hub.finalContact}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
